import React, { useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { createCustodyOverride } from '@coparent/shared/src/services/custodyService';
import { HiX, HiSwitchHorizontal } from 'react-icons/hi';
import toast from 'react-hot-toast';

const CustodyDayOverride = ({ date, custodyParentId, onClose }) => {
  const { currentUser } = useAuth();
  const { familyId, family, getCoParent, isHouseholdMember } = useFamily();
  const [reason, setReason] = useState('swap');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const coParent = getCoParent();

  const handlePropose = async () => {
    if (!familyId || !coParent) return;
    setLoading(true);
    try {
      const currentCustodyParent = custodyParentId || currentUser.uid;
      const swapToParent = currentCustodyParent === currentUser.uid ? coParent.id : currentUser.uid;

      await createCustodyOverride(familyId, {
        date: date,
        originalParentId: currentCustodyParent,
        newParentId: swapToParent,
        reason,
        note,
        status: 'proposed',
        proposedBy: currentUser.uid,
        respondedBy: null
      });
      toast.success('Swap proposed!');
      onClose();
    } catch (error) {
      toast.error('Failed to propose swap');
    }
    setLoading(false);
  };

  if (isHouseholdMember) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-ink-900">
              {format(date, 'EEEE, MMMM d')}
            </h3>
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
              <HiX className="text-lg" />
            </button>
          </div>
          <p className="text-sm text-ink-400 text-center py-4">Custody swaps can only be proposed by the primary co-parents.</p>
          <button onClick={onClose} className="btn-secondary w-full mt-2">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-ink-900">
            {format(date, 'EEEE, MMMM d')}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
            <HiX className="text-lg" />
          </button>
        </div>

        <div className="flex items-center gap-3 p-3.5 bg-parent1-50/60 rounded-xl mb-5 border border-parent1-100">
          <HiSwitchHorizontal className="text-parent1-600 text-lg" />
          <div>
            <p className="text-sm font-semibold text-ink-900">Propose a swap</p>
            <p className="text-xs text-ink-400">
              {custodyParentId === currentUser.uid
                ? `Ask ${coParent?.displayName || 'co-parent'} to take this day`
                : `Request this day from ${coParent?.displayName || 'co-parent'}`
              }
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Reason</label>
            <select
              value={reason}
              onChange={e => setReason(e.target.value)}
              className="input-base"
            >
              <option value="swap">Swap days</option>
              <option value="vacation">Vacation</option>
              <option value="work">Work conflict</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Note (optional)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              className="input-base resize-none"
              placeholder="Add a note..."
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">
            Cancel
          </button>
          <button
            onClick={handlePropose}
            disabled={loading}
            className="btn-primary flex-1"
          >
            {loading ? 'Sending...' : 'Propose Swap'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustodyDayOverride;
